import CountryField from '@/components/Forms/Fields/CountryField.tsx';
import PhoneField from '@/components/Forms/Fields/PhoneField/PhoneField.tsx';
import TurnstileField from '@/components/Forms/Fields/TurnstileField.tsx';
import usePartnerRegistrationForm, {
  PartnerRegistrationFormType,
} from '@/components/Forms/PartnerRegistration/usePartnerRegistrationForm.ts';
import ThemeButton from '@/theme/Components/ThemeButton/ThemeButton.tsx';
import { Col, Row } from '@capitalcloud-investhub/design-system';
import { Checkbox, Flex, Form, Input, Select, Typography } from 'antd';

const { Item } = Form;

interface PartnerRegistrationFormProps {
  onSubmit: (values: PartnerRegistrationFormType) => void;
  initialValues?: Partial<PartnerRegistrationFormType>;
  referralOptions?: { value: string; label: string }[];
  cguUrl?: string;
  onLoginClick?: () => void;
  loading?: boolean;
  /** `Partners - Orias facultatif` — true: ORIAS facultatif & SIREN requis ; false: ORIAS requis. */
  oriasOptional?: boolean;
  /** `Code partenaire à l'inscription réseau` — affiche le champ "code partenaire". */
  showPartnerCode?: boolean;
  /** Clé publique Cloudflare Turnstile ; vide => aucun captcha. */
  turnstileSiteKey?: string;
  /** Langue du widget Turnstile (langue courante de la page). */
  language?: string;
  /** Incrémenté par l'appelant pour réinitialiser le captcha après un échec de soumission. */
  captchaResetKey?: number;
}

const PartnerRegistrationForm = ({
  onSubmit,
  initialValues,
  referralOptions,
  cguUrl = '#',
  onLoginClick,
  loading,
  oriasOptional = false,
  showPartnerCode = false,
  turnstileSiteKey = '',
  language,
  captchaResetKey,
}: PartnerRegistrationFormProps) => {
  const { form, onFinish, defaultReferralOptions, t } =
    usePartnerRegistrationForm({
      onSubmit,
    });

  return (
    <Form
      autoComplete="off"
      form={form}
      id="partnerRegistrationForm"
      initialValues={initialValues}
      layout="vertical"
      name="partnerRegistrationForm"
      onFinish={onFinish}
    >
      <Item
        label={t('form.email_label')}
        name="email"
        rules={[
          { required: true, message: t('form.email_required') },
          { type: 'email', message: t('form.email_format_error') },
        ]}
      >
        <Input placeholder={t('form.email_placeholder')} />
      </Item>

      <Item
        label={t('form.entity_name_label')}
        name="name"
        rules={[{ required: true, message: t('form.entity_name_required') }]}
      >
        <Input placeholder={t('form.entity_name_placeholder')} />
      </Item>

      <Item
        label={t('form.siren_label')}
        name="siren"
        rules={
          oriasOptional
            ? [{ required: true, message: t('form.siren_required') }]
            : []
        }
      >
        <Input placeholder={t('form.siren_placeholder')} />
      </Item>

      <Item
        label={t('form.orias_label')}
        name="orias"
        rules={
          oriasOptional
            ? []
            : [{ required: true, message: t('form.orias_required') }]
        }
      >
        <Input placeholder={t('form.orias_placeholder')} />
      </Item>

      {showPartnerCode && (
        <Item
          label={t('form.partner_code_label')}
          name="partner_code"
          rules={[
            { required: true, message: t('form.partner_code_required') },
          ]}
        >
          <Input placeholder={t('form.partner_code_placeholder')} />
        </Item>
      )}

      <Row gutter={16}>
        <Col sm={12} xs={24}>
          <Item
            label={t('form.contact_firstname_label')}
            name="contact_firstname"
            rules={[
              {
                required: true,
                message: t('form.contact_firstname_required'),
              },
            ]}
          >
            <Input placeholder={t('form.contact_firstname_placeholder')} />
          </Item>
        </Col>
        <Col sm={12} xs={24}>
          <Item
            label={t('form.contact_lastname_label')}
            name="contact_lastname"
            rules={[
              { required: true, message: t('form.contact_lastname_required') },
            ]}
          >
            <Input placeholder={t('form.contact_lastname_placeholder')} />
          </Item>
        </Col>
      </Row>

      <PhoneField
        label={t('form.phone_label')}
        name="telephone"
        placeholder={t('form.phone_placeholder')}
        requiredMessage={t('form.phone_required')}
      />

      <Item
        label={t('form.address_label')}
        name="address"
        rules={[
          { required: true, message: t('form.required_address_message') },
        ]}
      >
        <Input placeholder={t('form.address_placeholder')} />
      </Item>

      <Row gutter={16}>
        <Col sm={8} xs={24}>
          <Item
            label={t('form.zipcode_label')}
            name="zipcode"
            rules={[
              { required: true, message: t('form.required_zipcode_message') },
            ]}
          >
            <Input placeholder={t('form.zipcode_placeholder')} />
          </Item>
        </Col>
        <Col sm={16} xs={24}>
          <Item
            label={t('form.city_label')}
            name="city"
            rules={[
              { required: true, message: t('form.required_city_message') },
            ]}
          >
            <Input placeholder={t('form.city_placeholder')} />
          </Item>
        </Col>
      </Row>

      <CountryField
        label={t('form.country_label')}
        name="country"
        placeholder={t('form.country_placeholder')}
        requiredMessage={t('form.country_required')}
      />

      <Item
        label={t('form.password_label')}
        name="password"
        rules={[{ required: true, message: t('form.password_required') }]}
      >
        <Input.Password placeholder={t('form.password_placeholder')} />
      </Item>

      <Item
        dependencies={['password']}
        label={t('form.confirm_password_label')}
        name="password_confirm"
        rules={[
          { required: true, message: t('form.confirm_password_required') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(t('form.passwords_do_not_match')),
              );
            },
          }),
        ]}
      >
        <Input.Password placeholder={t('form.confirm_password_placeholder')} />
      </Item>

      <Item
        label={t('form.referral_label')}
        name="referral_source"
        rules={[{ required: true, message: t('form.referral_required') }]}
      >
        <Select
          optionFilterProp="label"
          options={referralOptions ?? defaultReferralOptions}
          placeholder={t('form.referral_placeholder')}
        />
      </Item>

      <Item
        name="accept_cgu"
        rules={[
          {
            validator: (_, checked) =>
              checked
                ? Promise.resolve()
                : Promise.reject(new Error(t('form.accept_cgu_required'))),
          },
        ]}
        valuePropName="checked"
      >
        <Checkbox>
          {t('form.accept_cgu_label')}{' '}
          <Typography.Link
            href={cguUrl}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
          >
            {t('form.cgu_link_label')}
          </Typography.Link>
        </Checkbox>
      </Item>

      <TurnstileField
        language={language}
        name="cf-turnstile-response"
        requiredMessage={t('form.captcha_required')}
        resetKey={captchaResetKey}
        siteKey={turnstileSiteKey}
      />

      <ThemeButton block htmlType="submit" loading={loading} type="primary">
        {t('form.create_account')}
      </ThemeButton>

      <Flex justify="center" style={{ paddingTop: 16 }}>
        <Typography.Link onClick={onLoginClick}>
          {t('form.already_have_account')}
        </Typography.Link>
      </Flex>
    </Form>
  );
};

export default PartnerRegistrationForm;
